import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Send, ChevronDown, CheckCircle2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { useBackTarget } from '../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = 'var(--accent-purple)';
const SUPPORT_REASONS = [
  'Payment Issue',
  'Account Issue',
  'Bug Report',
  'Report a venue',
  'Feedback',
  'FAQ / general help',
  'Other',
];

/**
 * Contact Support page — simplified in Feb 2026: user submits a ticket
 * with a reason + message, then sees a "Thanks, we'll get back to you"
 * confirmation. Once an admin replies, the ongoing conversation lives
 * in the regular Messenger under a "Support" filter, not on this page.
 */
export default function SupportContactPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const location = useLocation();
  const user = React.useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const initialReason = location.state?.reason || '';

  const [reason, setReason] = useState(initialReason || SUPPORT_REASONS[0]);
  const [showReasonMenu, setShowReasonMenu] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please write your message first');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/support/requests`,
        { reason, message: message.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send your message');
    } finally {
      setSending(false);
    }
  };

  const handleAnother = () => {
    setSubmitted(false);
    setMessage('');
    setReason(SUPPORT_REASONS[0]);
  };

  return (
    <div
      className="app-shell premium-page"
      data-testid="support-contact-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        backgroundImage:
          'radial-gradient(ellipse 600px 360px at 50% -140px, rgba(196, 18, 48, 0.10) 0%, rgba(196, 18, 48, 0) 60%)',
        position: 'relative',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 28,
      }}
    >
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0 0' }}>
        <button
          onClick={goBack}
          data-testid="support-contact-back-button"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            borderRadius: 999,
            cursor: 'pointer',
            padding: 6,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: 'var(--premium-shadow-nested)',
          }}
        >
          <ChevronLeft size={22} color="var(--text)" strokeWidth={2} />
        </button>
        <NotificationBellButton
          buttonTestId="support-contact-bell-button"
          popupTestId="support-contact-notification-popup"
        />
      </div>

      <RouteTransitionLogo width={86} height={76} dataTestId="support-contact-shared-logo" style={{ marginTop: 18 }} />
      <h1 style={{ fontFamily: FONT, fontSize: 24, fontWeight: 400, color: 'var(--text)', margin: '8px 0 2px' }}>
        Contact Support
      </h1>
      <p style={{ margin: 0, fontFamily: SUB_FONT, fontSize: 12.5, color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Clock size={12} strokeWidth={2} color={BRAND} />
        Real humans · usually replying within 2 hours
      </p>

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            data-testid="support-contact-thanks-card"
            style={{
              width: '100%',
              maxWidth: 360,
              marginTop: 26,
              background: 'var(--surface)',
              border: '1px solid var(--card-border)',
              borderRadius: 22,
              boxShadow: 'var(--premium-shadow-main)',
              padding: '26px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 12,
            }}
          >
            <div style={{
              width: 60, height: 60, borderRadius: 999,
              background: 'rgba(31, 142, 42, 0.14)',
              color: '#1F8E2A',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CheckCircle2 size={30} strokeWidth={2.2} />
            </div>
            <p style={{ margin: 0, fontFamily: FONT, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
              Thanks for reaching out
            </p>
            <p style={{ margin: 0, fontFamily: SUB_FONT, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5, maxWidth: 300 }}>
              We received your message. As soon as an admin replies, a new conversation will appear in your Messages under the <strong>Support</strong> filter — check it there.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleAnother}
                data-testid="support-contact-another-button"
                style={{
                  padding: '10px 16px',
                  borderRadius: 999,
                  border: '1px solid var(--card-border)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontFamily: SUB_FONT,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Send another
              </button>
              <button
                type="button"
                onClick={() => {
                  const role = (user?.role || 'guest').toLowerCase();
                  const dest = role === 'host'
                    ? '/host/messages'
                    : role === 'venue_owner' || role === 'venue'
                      ? '/venue/messages'
                      : '/guest/share';
                  navigate(dest);
                }}
                data-testid="support-contact-open-messages-button"
                style={{
                  padding: '10px 16px',
                  borderRadius: 999,
                  border: 'none',
                  background: BRAND,
                  color: '#FFFFFF',
                  fontFamily: SUB_FONT,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Go to Messages
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            style={{
              width: '100%',
              maxWidth: 360,
              marginTop: 22,
              background: 'var(--surface)',
              border: '1px solid var(--card-border)',
              borderRadius: 22,
              boxShadow: 'var(--premium-shadow-main)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '16px 16px 0' }}>
              <label style={{ display: 'block', fontFamily: SUB_FONT, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 8 }}>
                Reason
              </label>
              <div style={{ position: 'relative' }}>
                <button
                  data-testid="support-contact-reason-select"
                  onClick={() => setShowReasonMenu((s) => !s)}
                  style={{
                    width: '100%',
                    background: 'var(--accent-surface)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 14,
                    padding: '12px 14px',
                    fontFamily: SUB_FONT,
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: 'var(--text)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                >
                  <span>{reason}</span>
                  <ChevronDown size={16} color="#5A5570" style={{ transform: showReasonMenu ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }} />
                </button>
                <AnimatePresence>
                  {showReasonMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.18 }}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        right: 0,
                        background: 'var(--surface)',
                        border: '1px solid var(--card-border)',
                        borderRadius: 14,
                        boxShadow: 'var(--premium-shadow-main)',
                        zIndex: 10,
                        overflow: 'hidden',
                      }}
                    >
                      {SUPPORT_REASONS.map((r) => (
                        <button
                          key={r}
                          data-testid={`support-reason-option-${r.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          onClick={() => { setReason(r); setShowReasonMenu(false); }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '11px 14px',
                            background: r === reason ? 'var(--accent-surface)' : 'var(--surface)',
                            border: 'none',
                            fontFamily: SUB_FONT,
                            fontSize: 14,
                            color: 'var(--text)',
                            cursor: 'pointer',
                            fontWeight: r === reason ? 700 : 500,
                          }}
                        >
                          {r}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div style={{ padding: '14px 16px 16px', display: 'flex', gap: 10, alignItems: 'flex-end', background: 'var(--surface)' }}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us how we can help…"
                rows={4}
                data-testid="support-contact-message-input"
                style={{
                  flex: 1,
                  border: '1px solid var(--card-border)',
                  borderRadius: 16,
                  padding: '11px 14px',
                  fontFamily: SUB_FONT,
                  fontSize: 14,
                  resize: 'none',
                  minHeight: 110,
                  maxHeight: 160,
                  outline: 'none',
                  transition: 'border-color 180ms ease, box-shadow 180ms ease',
                  background: 'var(--surface)',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--accent-purple)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-surface)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--card-border)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              <button
                onClick={handleSubmit}
                disabled={sending || !message.trim()}
                data-testid="support-contact-submit-button"
                style={{
                  flexShrink: 0,
                  width: 46,
                  height: 46,
                  borderRadius: 999,
                  border: 'none',
                  background: sending || !message.trim() ? 'var(--accent-surface-muted)' : 'var(--accent-purple)',
                  color: '#FFFFFF',
                  cursor: sending || !message.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: sending || !message.trim() ? 'none' : 'var(--premium-shadow-main)',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                }}
              >
                <Send size={18} strokeWidth={2.2} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ flex: 1, minHeight: 14 }} />

      <p
        data-testid="support-contact-account-id"
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11.5,
          color: 'var(--text-2)',
          margin: '14px 0 0',
        }}
      >
        Account · {user.customer_id || '—'}
      </p>
    </div>
  );
}
